import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { SearchSelect } from 'components/core/search_select';
import { DropdownMenu } from './dropdown_menu';
import { SelectedSuggestions } from './selected_suggestions';
import { SelectedSuggestionsContainer } from './selected_suggestions_container';
import { fetchInitialValue, fetchSuggestions, resetState, setEntities, setInitialSelection } from './slice';
const wrapperCss = "wmmef15";
const Wrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, props.className)} />;
});
const helpContainerCss = "h101y7ds";
const HelpContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(helpContainerCss, props.className)} />;
});
const HelpText = ({
  children
}) => <HelpContainer>
    <BodyText color="muted">{children}</BodyText>
  </HelpContainer>;
const DEFAULT_ENTRIES = ['memberships', 'invitations', 'migrations'];
export const MembershipsPicker = ({
  inputPlaceholder = null,
  notFoundPlaceholderTitle = null,
  notFoundPlaceholderText = null,
  getUnavailabilityReason = () => null,
  getSideEffectInfo = () => null,
  isSuggestionRemovable = () => true,
  isSuggestionDisabled = () => false,
  initialValue = null,
  initialSelection = null,
  entities = DEFAULT_ENTRIES,
  selectedSuggestionsComponent = SelectedSuggestions,
  showUserRole = false,
  showOrgAdminRemoval = false,
  filterForGroupAdmin = false,
  includeOnlyGroupMembers = false,
  userGroupId = null,
  userGroupIds = [],
  onSelect,
  onRemove,
  autoFocus = true,
  helpText,
  disabled
}) => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const searchField = useRef();
  useEffect(() => {
    dispatch(setEntities(entities));
  }, [dispatch, entities]);
  useEffect(() => () => dispatch(resetState()), [dispatch]);
  useEffect(() => {
    if (initialSelection) {
      dispatch(setInitialSelection(initialSelection));
    }
  }, [dispatch, initialSelection]);
  useEffect(() => {
    if (initialValue) {
      dispatch(fetchInitialValue(initialValue));
    }
  }, [dispatch, initialValue]);
  const handleSearch = useCallback(value => {
    dispatch(fetchSuggestions({
      query: value,
      filterForGroupAdmin,
      includeOnlyGroupMembers,
      userGroupId,
      userGroupIds
    }));
  }, [dispatch, filterForGroupAdmin, includeOnlyGroupMembers, userGroupId, userGroupIds]);
  const handleSearchStarted = useCallback(value => {
    if (!value) return;
    dispatch(fetchSuggestions.pending(value));
  }, [dispatch]);
  const handleSelection = selection => {
    setQuery('');
    searchField.current.focus();
    if (onSelect) onSelect(selection.entity);
  };
  const updateQuery = e => {
    const value = e.target.value;
    setQuery(value);
  };
  return <>
      <Wrapper>
        <SearchSelect ref={searchField} name="member_search" placeholder={inputPlaceholder} value={query} onChange={updateQuery} onSearch={handleSearch} onSearchStarted={handleSearchStarted} autoFocus={autoFocus} disabled={disabled}>
          {query && <DropdownMenu showUserRole={showUserRole} notFoundPlaceholderTitle={notFoundPlaceholderTitle} notFoundPlaceholderText={notFoundPlaceholderText} getUnavailabilityReason={getUnavailabilityReason} getSideEffectInfo={getSideEffectInfo} showOrgAdminRemoval={showOrgAdminRemoval} onSelect={handleSelection} />}
        </SearchSelect>
      </Wrapper>
      {helpText && <HelpText>{helpText}</HelpText>}
      <SelectedSuggestionsContainer component={selectedSuggestionsComponent} isSuggestionRemovable={isSuggestionRemovable} isSuggestionDisabled={isSuggestionDisabled} showUserRole={showUserRole} showOrgAdminRemoval={showOrgAdminRemoval} onRemove={onRemove} />
    </>;
};
MembershipsPicker.propTypes = {
  inputPlaceholder: PropTypes.string,
  notFoundPlaceholderTitle: PropTypes.string,
  notFoundPlaceholderText: PropTypes.node,
  getUnavailabilityReason: PropTypes.func,
  getSideEffectInfo: PropTypes.func,
  filterForGroupAdmin: PropTypes.bool,
  includeOnlyGroupMembers: PropTypes.bool,
  userGroupId: PropTypes.number,
  userGroupIds: PropTypes.arrayOf(PropTypes.number),
  isSuggestionRemovable: PropTypes.func,
  isSuggestionDisabled: PropTypes.func,
  showUserRole: PropTypes.bool,
  showOrgAdminRemoval: PropTypes.bool,
  initialSelection: PropTypes.shape({
    organizationMembershipIds: PropTypes.arrayOf(PropTypes.number),
    organizationInvitationIds: PropTypes.arrayOf(PropTypes.number),
    userGroupIds: PropTypes.arrayOf(PropTypes.number),
    migrations: PropTypes.arrayOf(PropTypes.number)
  }),
  initialValue: PropTypes.shape({
    organizationMembershipIds: PropTypes.arrayOf(PropTypes.number),
    organizationInvitationIds: PropTypes.arrayOf(PropTypes.number),
    userGroupIds: PropTypes.arrayOf(PropTypes.number),
    migrations: PropTypes.arrayOf(PropTypes.number)
  }),
  entities: PropTypes.arrayOf(PropTypes.oneOf(['memberships', 'invitations', 'userGroups', 'migrations'])),
  selectedSuggestionsComponent: PropTypes.elementType,
  onSelect: PropTypes.func,
  onRemove: PropTypes.func,
  autoFocus: PropTypes.bool,
  helpText: PropTypes.string,
  disabled: PropTypes.bool
};

require("./memberships_picker.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./memberships_picker.js");